import * as Popover from '@radix-ui/react-popover';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { Crown, Flame, Gamepad2, Gem, Heart, Leaf, Lock, Moon, Music, Shield, Skull, Star, Zap, type LucideIcon } from 'lucide-react';
import type { SpaceTag, SpaceTagCard, TagBadge, UserTag } from '@spawnwire/shared';
import { Button, clsx, colorFor, Spinner } from '../../components/ui';
import { ApiError, errorMessage, http } from '../../lib/api';
import { CATEGORY_META } from '../../lib/discover';
import { useStore } from '../../store/store';
import { ui, useUi } from '../../store/ui';
import { SpaceIcon } from '../shell/SpaceIcon';

/** The icon in a tag chip and its colour. */
export const TAG_BADGE_META: Record<TagBadge, { icon: LucideIcon; color: string; label: string }> = {
  bolt: { icon: Zap, color: '#f0b232', label: 'Bolt' },
  star: { icon: Star, color: '#f59e0b', label: 'Star' },
  heart: { icon: Heart, color: '#ed4245', label: 'Heart' },
  flame: { icon: Flame, color: '#f97316', label: 'Flame' },
  leaf: { icon: Leaf, color: '#22c55e', label: 'Leaf' },
  gem: { icon: Gem, color: '#38bdf8', label: 'Gem' },
  crown: { icon: Crown, color: '#facc15', label: 'Crown' },
  shield: { icon: Shield, color: '#6b8cff', label: 'Shield' },
  moon: { icon: Moon, color: '#a78bfa', label: 'Moon' },
  music: { icon: Music, color: '#eb459e', label: 'Music' },
  gamepad: { icon: Gamepad2, color: '#5865f2', label: 'Gamepad' },
  skull: { icon: Skull, color: '#b9bbbe', label: 'Skull' },
};

/** The small rounded chip with a tag's icon and text. Display only; see SpaceTagBadge for the clickable one. */
export function TagChip({ tag, className }: { tag: SpaceTag; className?: string }) {
  const { icon: Icon, color } = TAG_BADGE_META[tag.badge] ?? TAG_BADGE_META.bolt;
  return (
    <span className={clsx('inline-flex h-[18px] shrink-0 items-center gap-0.5 rounded-[5px] border border-line-strong/60 bg-active px-1 align-middle text-[11px] font-bold leading-none text-fg', className)}>
      <Icon className="size-3" style={{ color }} fill={color} strokeWidth={1.5} aria-hidden />
      <span className="whitespace-pre font-mono tracking-tight">{tag.text}</span>
    </span>
  );
}

/** Someone's space tag, which opens a card about the space when clicked. */
export function SpaceTagBadge({ tag, className }: { tag: UserTag; className?: string }) {
  const [open, setOpen] = useState(false);
  return (
    <Popover.Root open={open} onOpenChange={setOpen}>
      <Popover.Trigger asChild>
        <button type="button" aria-label={`Space tag ${tag.text}`} className={clsx('shrink-0 self-center rounded-[5px] transition-[filter] hover:brightness-125', className)}>
          <TagChip tag={tag} />
        </button>
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Content
          side="bottom"
          align="start"
          sideOffset={6}
          collisionPadding={8}
          className="animate-pop z-[75] w-[300px] max-w-[calc(100vw-16px)] overflow-hidden rounded-xl border border-line bg-sidebar shadow-pop outline-none"
        >
          <TagCard spaceId={tag.spaceId} close={() => setOpen(false)} />
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}

function TagCard({ spaceId, close }: { spaceId: string; close: () => void }) {
  const navigate = useNavigate();
  const [card, setCard] = useState<SpaceTagCard | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [joining, setJoining] = useState(false);
  const member = useStore((s) => !!s.spaces[spaceId]);
  const profileOpen = useUi((s) => s.dialog?.kind === 'profile');

  useEffect(() => {
    let live = true;
    http
      .get<SpaceTagCard>(`/spaces/${spaceId}/tag-card`)
      .then((c) => live && setCard(c))
      .catch((err) => live && setError(err instanceof ApiError && err.status === 404 ? 'This space no longer has a tag.' : errorMessage(err)));
    return () => {
      live = false;
    };
  }, [spaceId]);

  const go = (path: string) => {
    close();
    if (profileOpen) ui.close();
    navigate(path);
  };

  async function join() {
    if (!card) return;
    setJoining(true);
    try {
      const res = await http.post<{ spaceId: string; channelId: string | null }>(`/discover/${card.id}/join`);
      ui.toast(`Welcome to ${card.name}!`, 'success');
      go(res.channelId ? `/app/${res.spaceId}/${res.channelId}` : `/app/${res.spaceId}`);
    } catch (err) {
      ui.toast(errorMessage(err), 'error');
      setJoining(false);
    }
  }

  if (error) return <p className="p-4 text-sm text-muted">{error}</p>;
  if (!card) {
    return (
      <div className="flex justify-center p-8">
        <Spinner />
      </div>
    );
  }

  const accent = card.category ? CATEGORY_META[card.category].color : colorFor(card.id);
  const joined = member || card.joined;
  const est = new Date(card.createdAt).toLocaleDateString(undefined, { month: 'short', year: 'numeric' });

  return (
    <>
      <div className="relative h-[88px]" style={{ background: `linear-gradient(135deg, ${accent}, ${accent}aa)` }}>
        {card.bannerUrl && <img src={card.bannerUrl} alt="" className="absolute inset-0 size-full object-cover" draggable={false} />}
      </div>
      <div className="px-4 pb-4">
        <div className="relative -mt-9 inline-flex rounded-[30%] border-[5px] border-sidebar bg-sidebar">
          <SpaceIcon name={card.name} iconUrl={card.iconUrl} size={64} active />
        </div>
        <div className="mt-2 flex min-w-0 items-center gap-2">
          <h3 className="min-w-0 truncate text-lg font-bold text-fg" title={card.name}>
            {card.name}
          </h3>
          <TagChip tag={card.tag} />
        </div>
        <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-muted">
          <span className="flex items-center gap-1.5">
            <span className="size-2 rounded-full bg-online" aria-hidden />
            {card.onlineCount.toLocaleString()} Online
          </span>
          <span className="flex items-center gap-1.5">
            <span className="size-2 rounded-full bg-offline" aria-hidden />
            {card.memberCount.toLocaleString()} {card.memberCount === 1 ? 'Member' : 'Members'}
          </span>
        </div>
        <p className="mt-1 text-sm text-muted">Est. {est}</p>
        {joined ? (
          <Button className="mt-4 w-full !bg-success text-white hover:brightness-110" onClick={() => go(`/app/${card.id}`)}>
            Go to Server
          </Button>
        ) : card.joinable ? (
          <Button className="mt-4 w-full !bg-success text-white hover:brightness-110" loading={joining} onClick={() => void join()}>
            Join Server
          </Button>
        ) : (
          <>
            <Button className="mt-4 w-full" variant="secondary" icon={Lock} disabled>
              Invite only
            </Button>
            <p className="mt-2 text-center text-xs text-muted">This space isn't on Discover. Ask a member for an invite to join.</p>
          </>
        )}
      </div>
    </>
  );
}
